import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { CheckCircle2, CreditCard, Banknote, Phone, ShieldCheck, Loader2, Navigation, RefreshCw, Gift } from 'lucide-react';

export default function CheckoutPage() {
  const { cartItems, cartTotal, fetchCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('address'); // address, phone, payment, coc-otp, success
  const [orderResult, setOrderResult] = useState(null);
  const [preview, setPreview] = useState({ delivery_charge: 20, platform_fee: 0, free_delivery: false, free_threshold: 0, order_number: 0 });
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [address, setAddress] = useState({ first_name: '', last_name: '', full_address: '', door_no: '', city: '', state: '', pincode: '', mobile: '', address_type: 'Home' });
  const [geoLoc, setGeoLoc] = useState({ lat: null, lng: null });
  const [capturingLoc, setCapturingLoc] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);

  // Phone verification (Twilio — independent of Supabase Auth)
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [phoneResendIn, setPhoneResendIn] = useState(0);
  const [skipPhone, setSkipPhone] = useState(false);

  // COC
  const [cocOtp, setCocOtp] = useState('');
  const [cocOrderId, setCocOrderId] = useState('');
  const [cocResendIn, setCocResendIn] = useState(0);
  const [resendingCoc, setResendingCoc] = useState(false);

  const resendTimerRef = useRef(null);
  const initializedRef = useRef(false);

  // On mount: if a COC order is pending in localStorage, resume the OTP screen.
  useEffect(() => {
    const pending = localStorage.getItem('orgaanix_coc_pending');
    if (pending) {
      try {
        const p = JSON.parse(pending);
        // expires after 35 minutes (OTP is 30 min)
        if (p.order_id && p.created_at && (Date.now() - p.created_at) < 35 * 60 * 1000) {
          setCocOrderId(p.order_id);
          setStep('coc-otp');
        } else {
          localStorage.removeItem('orgaanix_coc_pending');
        }
      } catch { localStorage.removeItem('orgaanix_coc_pending'); }
    }
  }, []);
useEffect(() => {
  // CRITICAL: only run ONCE when user first loads
  // Using initializedRef prevents this from re-running when Supabase
  // refreshes the auth token and updates the user object reference,
  // which was overriding the coc-otp step back to payment
  if (user && !initializedRef.current) {
    initializedRef.current = true;

    const alreadyVerified = !!user?.verified_phone;
    if (alreadyVerified) {
      setPhoneNumber(user.verified_phone);
      setPhoneVerified(true);
    }

    api.get('/addresses').then(r => {
      setSavedAddresses(r.data || []);
      const def = (r.data || []).find(a => a.is_default) || (r.data || [])[0];

      // Never override step if a COC order is already pending
      const hasPendingCoc = !!localStorage.getItem('orgaanix_coc_pending');

      if (def) {
        setSelectedAddressId(def.id);
        setAddress({
          first_name: def.first_name || '',
          last_name: def.last_name || '',
          full_address: def.full_address || '',
          door_no: def.door_no || '',
          city: def.city || '',
          state: def.state || '',
          pincode: def.pincode || '',
          mobile: def.mobile || '',
          address_type: def.address_type || 'Home',
        });
        if (def.latitude) setGeoLoc({ lat: parseFloat(def.latitude), lng: parseFloat(def.longitude) });

        // Only advance step if not already in COC OTP flow
        if (!hasPendingCoc) {
          setStep(alreadyVerified ? 'payment' : 'phone');
        }
      } else {
        if (!hasPendingCoc) {
          setShowAddressForm(true);
        }
      }
    }).catch(() => {
      const hasPendingCoc = !!localStorage.getItem('orgaanix_coc_pending');
      if (!hasPendingCoc) {
        setShowAddressForm(true);
      }
    });
  }
}, [user]);

  // Fetch preview on cart/address change
useEffect(() => {
    if (!user) return;
    // Debounce — only fetch if not in coc-otp or success step
    if (step === 'coc-otp' || step === 'success') return;
    const phone = phoneNumber || address.mobile || '';
    const timer = setTimeout(() => {
      api.get('/checkout/preview', { params: { phone } })
        .then(r => setPreview(r.data))
        .catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, [cartTotal, phoneNumber, address.mobile, user, step]); 
  // Resend countdowns
  useEffect(() => {
    if (phoneResendIn <= 0) return;
    const t = setTimeout(() => setPhoneResendIn(phoneResendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [phoneResendIn]);
  useEffect(() => {
    if (cocResendIn <= 0) return;
    const t = setTimeout(() => setCocResendIn(cocResendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [cocResendIn]);

  const actualDeliveryCharge = preview.delivery_charge;
  const platformFee = preview.platform_fee;
  const grandTotal = cartTotal + actualDeliveryCharge + platformFee;

  const captureLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setCapturingLoc(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success('Location captured');
        // Reverse geocode (lightweight via public API)
        fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`)
          .then(r => r.json())
          .then(g => {
            const a = g.address || {};
            setAddress(prev => ({
              ...prev,
              full_address: prev.full_address || g.display_name || '',
              city: prev.city || a.city || a.town || a.village || '',
              state: prev.state || a.state || '',
              pincode: prev.pincode || a.postcode || '',
            }));
          }).catch(() => {});
        setCapturingLoc(false);
      },
      () => { toast.error('Could not get location'); setCapturingLoc(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Phone OTP via Twilio backend — never touches Supabase Auth
  const sendPhoneOtp = async () => {
    const formatted = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber.replace(/^0/, '')}`;
    setSendingOtp(true);
    try {
      await api.post('/phone/send-otp', { phone: formatted });
      setPhoneOtpSent(true);
      setPhoneResendIn(60);
      toast.success('OTP sent to your phone!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally { setSendingOtp(false); }
  };

  const verifyPhoneOtp = async () => {
    const formatted = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber.replace(/^0/, '')}`;
    try {
      await api.post('/phone/verify-otp', { phone: formatted, otp: phoneOtp });
      setPhoneVerified(true);
      toast.success('Phone verified!');
      setStep('payment');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    }
  };

  const saveAddressToProfile = async (addr, loc) => {
    try {
      if (selectedAddressId) {
        await api.put(`/addresses/${selectedAddressId}`, {
          ...addr, latitude: loc.lat, longitude: loc.lng, is_default: true,
        });
      } else {
        const res = await api.post('/addresses', {
          ...addr, latitude: loc.lat, longitude: loc.lng, is_default: true,
        });
        if (res.data?.id) setSelectedAddressId(res.data.id);
      }
    } catch {
      // non-fatal — migration may be pending
    }
  };

  const handleAddressSubmit = (e) => {
    e.preventDefault();
    if (!address.first_name || !address.mobile || !address.full_address || !address.pincode) {
      toast.error('Please fill all required fields'); return;
    }
    // Save address for future orders
    saveAddressToProfile(address, geoLoc);
    setShowAddressForm(false);
    // If skip_phone or already verified, go to payment
    if (phoneVerified || skipPhone) {
      setStep('payment');
    } else {
      setPhoneNumber(address.mobile);
      setStep('phone');
    }
  };

  // Razorpay Payment
  const handleRazorpay = async () => {
    setLoading(true);
    try {
      const res = await api.post('/payments/create-order', {
        shipping_address: address,
        customer_phone: phoneNumber || address.mobile,
        location_lat: geoLoc.lat,
        location_lng: geoLoc.lng,
      });
      const options = {
        key: res.data.key_id,
        amount: res.data.amount,
        currency: res.data.currency,
        name: 'Orgaanix',
        description: 'Fresh Organic Produce',
        order_id: res.data.razorpay_order_id,
        handler: async (response) => {
          try {
            const verify = await api.post('/payments/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setOrderResult({ order_id: verify.data.order_id, method: 'razorpay' });
            setStep('success');
            await fetchCart();
            toast.success('Payment successful!');
          } catch { toast.error('Payment verification failed'); }
        },
        prefill: { name: user?.name || '', email: user?.email || '', contact: phoneNumber || address.mobile },
        theme: { color: '#3d5a34' },
        modal: { ondismiss: () => { setLoading(false); toast.error('Payment cancelled'); } }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally { setLoading(false); }
  };

  // COC Payment
  const handleCOC = async () => {
    setLoading(true);
    try {
      const res = await api.post('/payments/coc-initiate', {
        shipping_address: address,
        customer_phone: phoneNumber || address.mobile,
        location_lat: geoLoc.lat,
        location_lng: geoLoc.lng,
      });
      setCocOrderId(res.data.order_id);
      setCocResendIn(60);
      // Persist so refresh keeps customer on the OTP screen
      localStorage.setItem('orgaanix_coc_pending', JSON.stringify({
        order_id: res.data.order_id, created_at: Date.now(),
      }));
      setStep('coc-otp');
      toast.success('Order placed! Our team will contact you with the OTP.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Order failed');
    } finally { setLoading(false); }
  };

  const verifyCOC = async () => {
    setLoading(true);
    try {
      await api.post('/payments/coc-verify', { order_id: cocOrderId, otp: cocOtp });
      localStorage.removeItem('orgaanix_coc_pending');
      setOrderResult({ order_id: cocOrderId, method: 'coc' });
      setStep('success');
      await fetchCart();
      toast.success('Payment confirmed!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Incorrect OTP. Please try again.');
      setCocOtp('');
      // Do NOT log out, do NOT clear cart, do NOT navigate away
    } finally { setLoading(false); }
  };

  const resendCocOtp = async () => {
    setResendingCoc(true);
    try {
      await api.post('/payments/coc-resend-otp', { order_id: cocOrderId });
      setCocResendIn(60);
      toast.success('New OTP sent to our team');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed');
    } finally { setResendingCoc(false); }
  };

  // Success
  if (step === 'success' && orderResult) {
    // Ensure no stale pending order persisted
    if (localStorage.getItem('orgaanix_coc_pending')) {
      localStorage.removeItem('orgaanix_coc_pending');
    }
    return (
      <div data-testid="order-success" className="max-w-md mx-auto px-4 py-20 text-center">
        <CheckCircle2 className="h-16 w-16 mx-auto text-primary mb-6" />
        <h2 className="text-2xl font-serif font-medium mb-2">Order Confirmed!</h2>
        <p className="text-muted-foreground mb-2">Order ID: <strong>{orderResult.order_id}</strong></p>
        <p className="text-sm text-muted-foreground mb-8">{orderResult.method === 'razorpay' ? 'Payment received via Razorpay.' : 'Cash payment verified.'}</p>
        <div className="flex gap-3 justify-center">
          <Button data-testid="view-orders-button" onClick={() => navigate('/orders')} className="rounded-full">View My Orders</Button>
          <Button variant="outline" onClick={() => navigate('/')} className="rounded-full">Continue Shopping</Button>
        </div>
      </div>
    );
  }

  // COC OTP Step
  if (step === 'coc-otp') {
    return (
      <div data-testid="coc-otp-step" className="max-w-md mx-auto px-4 py-16 text-center">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
          <Banknote className="h-7 w-7 text-primary" />
        </div>
        <h2 className="text-2xl font-serif font-medium mb-2">Confirm Your Cash Payment</h2>
        <p className="text-sm text-muted-foreground mb-1">
          Your order <strong className="text-foreground">{cocOrderId}</strong> has been placed.
        </p>
        <p className="text-sm text-muted-foreground mb-6">
          Our team will contact you shortly. Ask our delivery representative for the 6-digit OTP to confirm your cash payment.
        </p>
        <div className="bg-card rounded-3xl border p-6 text-left space-y-5 shadow-sm">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">OTP from our team</Label>
            <Input data-testid="coc-otp-input"
              value={cocOtp}
              onChange={e => setCocOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
              placeholder="\u2022 \u2022 \u2022 \u2022 \u2022 \u2022"
              maxLength={6}
              inputMode="numeric"
              autoFocus
              className="mt-2 rounded-2xl text-center text-2xl tracking-[0.5em] font-semibold h-14"
            />
          </div>
          <Button data-testid="coc-verify-btn" onClick={verifyCOC} className="w-full rounded-full h-12" disabled={loading || cocOtp.length !== 6}>
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying...</> : 'Confirm Payment'}
          </Button>
          <Button
            data-testid="coc-resend-btn"
            variant="outline"
            onClick={resendCocOtp}
            className="w-full rounded-full"
            disabled={cocResendIn > 0 || resendingCoc}
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${resendingCoc ? 'animate-spin' : ''}`} />
            {cocResendIn > 0 ? `Resend OTP in ${cocResendIn}s` : 'Resend OTP'}
          </Button>
        </div>
        <button
          onClick={() => {
            if (!window.confirm('Cancel this pending COC order? Your cart will be kept.')) return;
            localStorage.removeItem('orgaanix_coc_pending');
            setStep('payment');
            setCocOrderId('');
            setCocOtp('');
          }}
          className="mt-5 text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          Cancel this order
        </button>
      </div>
    );
  }

  const AddressFormBlock = (
    <div className="bg-card rounded-3xl border border-border/50 p-6 shadow-sm mb-6">
      <h3 className="font-serif font-medium text-lg mb-5">Delivery Address</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label>First Name *</Label><Input data-testid="first-name" value={address.first_name} onChange={e => setAddress({...address, first_name: e.target.value})} className="mt-1.5 rounded-2xl" /></div>
        <div><Label>Last Name</Label><Input data-testid="last-name" value={address.last_name} onChange={e => setAddress({...address, last_name: e.target.value})} className="mt-1.5 rounded-2xl" /></div>
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between">
            <Label>Address *</Label>
            <Button type="button" data-testid="capture-location-btn" variant="ghost" size="sm" onClick={captureLocation} className="h-7 text-xs gap-1 text-primary" disabled={capturingLoc}>
              <Navigation className="h-3 w-3" /> {capturingLoc ? 'Locating...' : geoLoc.lat ? 'Update Location' : 'Use Current Location'}
            </Button>
          </div>
          <Input data-testid="full-address" value={address.full_address} onChange={e => setAddress({...address, full_address: e.target.value})} className="mt-1.5 rounded-2xl" />
          {geoLoc.lat && <p className="text-[10px] text-primary mt-1">✓ Precise location attached</p>}
        </div>
        <div><Label>Door / Flat No *</Label><Input data-testid="door-no" value={address.door_no} onChange={e => setAddress({...address, door_no: e.target.value})} className="mt-1.5 rounded-2xl" /></div>
        <div><Label>Pincode *</Label><Input data-testid="pincode" value={address.pincode} onChange={e => setAddress({...address, pincode: e.target.value})} className="mt-1.5 rounded-2xl" /></div>
        <div><Label>City</Label><Input data-testid="city" value={address.city} onChange={e => setAddress({...address, city: e.target.value})} className="mt-1.5 rounded-2xl" /></div>
        <div><Label>State</Label><Input value={address.state} onChange={e => setAddress({...address, state: e.target.value})} className="mt-1.5 rounded-2xl" /></div>
        <div><Label>Mobile Number *</Label><Input data-testid="mobile" value={address.mobile} onChange={e => setAddress({...address, mobile: e.target.value})} className="mt-1.5 rounded-2xl" /></div>
        <div>
          <Label>Address Type</Label>
          <div className="flex gap-2 mt-1.5">
            {['Home','Work','Other'].map(t => (
              <button type="button" key={t}
                data-testid={`address-type-${t.toLowerCase()}`}
                onClick={() => setAddress({...address, address_type: t})}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${address.address_type === t ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>
      {step === 'address' && <Button data-testid="continue-btn" type="submit" className="mt-6 rounded-full">Continue</Button>}
    </div>
  );

  return (
    <div data-testid="checkout-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl sm:text-4xl font-serif font-medium mb-8">Checkout</h1>
      <script src="https://checkout.razorpay.com/v1/checkout.js" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-6">
          {/* Saved address summary + change */}
          {!showAddressForm && selectedAddressId && (
            <div className="bg-card rounded-3xl border border-border/50 p-6 shadow-sm mb-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Deliver to</p>
                  <p className="font-medium text-sm">{address.first_name} {address.last_name}</p>
                  <p className="text-sm text-muted-foreground">{address.door_no}, {address.full_address}</p>
                  <p className="text-sm text-muted-foreground">{address.city} {address.state} {address.pincode}</p>
                  <p className="text-sm text-muted-foreground mt-1">Mobile: {address.mobile}</p>
                </div>
                <Button variant="outline" size="sm" data-testid="change-address-btn" onClick={() => setShowAddressForm(true)} className="rounded-full">Change</Button>
              </div>
            </div>
          )}
          {/* Address Form */}
          {showAddressForm && (
            <form onSubmit={handleAddressSubmit}>{AddressFormBlock}</form>
          )}

          {/* Step: Phone Verification (optional) */}
          {step === 'phone' && !showAddressForm && (
            <div data-testid="phone-verify-step" className="bg-card rounded-3xl border border-border/50 p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <Phone className="h-5 w-5 text-primary" />
                <h3 className="font-serif font-medium text-lg">Verify Mobile Number</h3>
                <span className="text-xs text-muted-foreground ml-auto">(Optional)</span>
              </div>
              {!phoneOtpSent ? (
                <div className="space-y-4">
                  <div><Label>Mobile Number</Label><Input data-testid="phone-input" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="10-digit number" className="mt-1.5 rounded-2xl" /></div>
                  <div className="flex gap-3">
                    <Button data-testid="send-phone-otp" onClick={sendPhoneOtp} className="rounded-full" disabled={sendingOtp || phoneNumber.length < 10}>
                      {sendingOtp ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : 'Send OTP'}
                    </Button>
                    <Button data-testid="skip-phone-btn" variant="ghost" onClick={() => { setSkipPhone(true); setStep('payment'); }} className="rounded-full text-muted-foreground">
                      Skip for now
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">OTP sent to {phoneNumber}</p>
                  <div><Label>Enter OTP</Label><Input data-testid="phone-otp-input" value={phoneOtp} onChange={e => setPhoneOtp(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="6-digit OTP" maxLength={6} className="mt-1.5 rounded-2xl text-center tracking-widest" /></div>
                  <div className="flex gap-3">
                    <Button data-testid="verify-phone-otp" onClick={verifyPhoneOtp} className="rounded-full" disabled={phoneOtp.length < 6}>Verify & Continue</Button>
                    <Button variant="outline" onClick={sendPhoneOtp} className="rounded-full" disabled={phoneResendIn > 0 || sendingOtp}>
                      {phoneResendIn > 0 ? `Resend in ${phoneResendIn}s` : 'Resend OTP'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: Payment */}
          {step === 'payment' && !showAddressForm && (
            <div data-testid="payment-step" className="space-y-4">
              {phoneVerified && (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl text-sm">
                  <ShieldCheck className="h-4 w-4 text-green-600" />
                  <span className="text-green-800">Phone verified: {phoneNumber}</span>
                  <button onClick={() => { setPhoneVerified(false); setPhoneOtpSent(false); setPhoneOtp(''); setStep('phone'); }} className="ml-auto text-xs text-green-600 hover:underline">Change</button>
                </div>
              )}
              {preview.free_delivery && (
                <div data-testid="free-delivery-banner" className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl text-sm">
                  <Gift className="h-4 w-4 text-amber-700" />
                  <span className="text-amber-900">Free delivery on your first {preview.free_threshold} orders! (Order #{preview.order_number})</span>
                </div>
              )}
              <div className="bg-card rounded-3xl border border-border/50 p-6 shadow-sm">
                <h3 className="font-serif font-medium text-lg mb-5">Payment Method</h3>
                <div className="space-y-3">
                  <button data-testid="select-razorpay" onClick={() => setPaymentMethod('razorpay')}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-colors text-left ${paymentMethod === 'razorpay' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                    <CreditCard className="h-5 w-5 text-primary shrink-0" />
                    <div><p className="font-medium text-sm">Pay Online</p><p className="text-xs text-muted-foreground">UPI, Cards, Netbanking via Razorpay</p></div>
                  </button>
                  <button data-testid="select-coc" onClick={() => setPaymentMethod('coc')}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-colors text-left ${paymentMethod === 'coc' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                    <Banknote className="h-5 w-5 text-primary shrink-0" />
                    <div><p className="font-medium text-sm">Cash on Counter</p><p className="text-xs text-muted-foreground">Pay cash to our representative</p></div>
                  </button>
                </div>
              </div>
              <Button data-testid="place-order-btn" size="lg" className="w-full rounded-full" disabled={loading}
                onClick={paymentMethod === 'razorpay' ? handleRazorpay : handleCOC}>
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</> : paymentMethod === 'razorpay' ? `Pay \u20B9${grandTotal} Online` : `Place Order - \u20B9${grandTotal}`}
              </Button>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-card rounded-3xl border border-border/50 p-6 sticky top-24 shadow-sm">
            <h3 className="font-serif font-medium text-lg mb-4">Order Summary</h3>
            <div className="space-y-3 mb-4">
              {cartItems.map((item) => (
                <div key={item.item_id} className="flex justify-between text-sm">
                  <span className="text-muted-foreground truncate mr-2">{item.product_name} x{item.quantity}</span>
                  <span className="font-medium shrink-0">{'\u20B9'}{item.price * item.quantity}</span>
                </div>
              ))}
            </div>
            <Separator />
            <div className="space-y-2 mt-4 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{'\u20B9'}{cartTotal}</span></div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Delivery</span>
                <span className={actualDeliveryCharge === 0 ? 'text-primary font-medium' : ''}>{actualDeliveryCharge === 0 ? 'Free' : `\u20B9${actualDeliveryCharge}`}</span>
              </div>
              {platformFee > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Platform Fee</span>
                  <span data-testid="platform-fee">{'\u20B9'}{platformFee}</span>
                </div>
              )}
            </div>
            <Separator className="my-4" />
            <div className="flex justify-between text-lg font-semibold"><span>Total</span><span data-testid="cart-total">{'\u20B9'}{grandTotal}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
